import { useState } from 'react';
import type { Aporte } from '../../lib/sesion';
import type { PropsVista } from './registro';
import { Ampliada } from './Ampliada';

/**
 * Vista `matriz`: matriz de validación impacto × esfuerzo de la Pantalla
 * (legible a 6 m). Cada propuesta aparece UNA vez, en el promedio de todas las
 * calificaciones de la sala (agregación `promedio` del manifiesto); solo
 * contenido anónimo y agregados (C7), jamás quién calificó. Respeta `opciones`
 * del manifiesto: `titulo`, `eje_x`, `eje_x_etiqueta`, `eje_y`,
 * `eje_y_etiqueta`, `min`, `max`, `etiqueta_punto` y `por_grupo` (si es true,
 * se muestra cuántas mesas calificaron — agregado, nunca por persona).
 */

/** Una propuesta ya consolidada: posición promedio y cuántas calificaciones. */
interface PuntoMatriz {
  /** Texto tal como lo escribió la sala la primera vez (para mostrar). */
  etiqueta: string;
  /** Promedios en unidades del manifiesto (min..max). */
  x: number;
  y: number;
  /** Cuántos aportes calificaron esta misma propuesta (badge ×N). */
  veces: number;
}

/** Clave de consolidación: minúsculas, sin tildes, espacios colapsados. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Lee un número finito del payload en la clave declarada, o null. */
function numeroDe(aporte: Aporte, clave: string): number | null {
  const v = (aporte.payload ?? {})[clave];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function VistaMatriz({ aportes, porGrupo, opciones }: PropsVista) {
  const titulo = typeof opciones.titulo === 'string' ? opciones.titulo : 'Así quedó la matriz';
  const ejeX = typeof opciones.eje_x === 'string' ? opciones.eje_x : 'esfuerzo';
  const ejeY = typeof opciones.eje_y === 'string' ? opciones.eje_y : 'impacto';
  const ejeXEtiqueta =
    typeof opciones.eje_x_etiqueta === 'string' ? opciones.eje_x_etiqueta : 'Esfuerzo que exige';
  const ejeYEtiqueta =
    typeof opciones.eje_y_etiqueta === 'string' ? opciones.eje_y_etiqueta : 'Impacto en el servicio';
  const min = typeof opciones.min === 'number' ? opciones.min : 1;
  const max = typeof opciones.max === 'number' && opciones.max > min ? opciones.max : min + 4;
  const etiquetaPunto =
    typeof opciones.etiqueta_punto === 'string' ? opciones.etiqueta_punto : 'propuesta';
  const mostrarGrupos = opciones.por_grupo === true;

  // Consolidación: la MISMA propuesta (texto normalizado) aparece una sola vez,
  // en el promedio de impacto y esfuerzo de todas sus calificaciones.
  const porPropuesta = new Map<
    string,
    { etiqueta: string; sumaX: number; sumaY: number; n: number }
  >();
  for (const a of aportes) {
    const crudo = (a.payload ?? {})[etiquetaPunto];
    if (typeof crudo !== 'string' || crudo.trim().length === 0) continue;
    const x = numeroDe(a, ejeX);
    const y = numeroDe(a, ejeY);
    if (x == null || y == null) continue; // sin ambos ejes numéricos no hay punto
    const clave = normalizar(crudo);
    const previo = porPropuesta.get(clave);
    if (previo) {
      previo.sumaX += x;
      previo.sumaY += y;
      previo.n += 1;
    } else {
      porPropuesta.set(clave, { etiqueta: crudo.trim(), sumaX: x, sumaY: y, n: 1 });
    }
  }
  if (porPropuesta.size === 0) return null;

  const puntos: PuntoMatriz[] = [...porPropuesta.values()].map((p) => ({
    etiqueta: p.etiqueta,
    x: p.sumaX / p.n,
    y: p.sumaY / p.n,
    veces: p.n,
  }));

  return (
    <MatrizConZoom
      titulo={titulo}
      puntos={puntos}
      min={min}
      max={max}
      ejeX={ejeX}
      ejeXEtiqueta={ejeXEtiqueta}
      ejeYEtiqueta={ejeYEtiqueta}
      mostrarGrupos={mostrarGrupos}
      porGrupo={porGrupo}
    />
  );
}

/** Matriz con zoom: clic en un punto lo lee a sala entera (Ampliada). */
function MatrizConZoom({
  titulo,
  puntos,
  min,
  max,
  ejeX,
  ejeXEtiqueta,
  ejeYEtiqueta,
  mostrarGrupos,
  porGrupo,
}: {
  titulo: string;
  puntos: PuntoMatriz[];
  min: number;
  max: number;
  ejeX: string;
  ejeXEtiqueta: string;
  ejeYEtiqueta: string;
  mostrarGrupos: boolean;
  porGrupo: PropsVista['porGrupo'];
}) {
  const [ampliado, setAmpliado] = useState<PuntoMatriz | null>(null);
  const rango = max - min;

  // % dentro del plano, con clamp por si un payload viejo trae valores fuera.
  const pct = (v: number) => Math.min(100, Math.max(0, ((v - min) / rango) * 100));
  const mesas = porGrupo.filter((g) => g.n > 0).length;

  // Lecturas de cuadrante. El manifiesto declara esfuerzo en X e impacto en Y;
  // si un manifiesto futuro los invirtiera, las lecturas se acomodan solas.
  const xEsEsfuerzo = ejeX !== 'impacto';
  const RAPIDAS = 'Rápidas'; // alto impacto · bajo esfuerzo
  const ESTRATEGICAS = 'Estratégicas'; // alto impacto · alto esfuerzo
  const OPCIONALES = 'Opcionales'; // bajo impacto · bajo esfuerzo
  const RECONSIDERAR = 'Reconsiderar'; // bajo impacto · alto esfuerzo
  const esquinas = xEsEsfuerzo
    ? { arribaIzq: RAPIDAS, arribaDer: ESTRATEGICAS, abajoIzq: OPCIONALES, abajoDer: RECONSIDERAR }
    : { arribaIzq: RECONSIDERAR, arribaDer: ESTRATEGICAS, abajoIzq: OPCIONALES, abajoDer: RAPIDAS };

  return (
    <section className="mt-10 w-full max-w-4xl">
      <h2 className="mb-6 text-center font-sans text-lg uppercase tracking-wide text-content-muted">
        {titulo}
      </h2>

      <div className="flex items-stretch justify-center gap-3">
        {/* Etiqueta del eje Y, vertical, pegada al plano */}
        <div className="flex items-center">
          <span
            className="font-sans text-lg uppercase tracking-wide text-content-muted"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {ejeYEtiqueta}
          </span>
        </div>

        {/* Plano de la matriz: cuadrado, alto máximo ~60vh */}
        <div className="w-full" style={{ maxWidth: '60vh' }}>
          <div className="relative aspect-square w-full rounded-lg border border-border-strong bg-surface-raised">
            {/* Líneas medias: parten el plano en 4 cuadrantes */}
            <div className="absolute inset-y-0 left-1/2 border-l border-border" aria-hidden />
            <div className="absolute inset-x-0 top-1/2 border-t border-border" aria-hidden />

            {/* Lecturas sobrias de cuadrante, en las cuatro esquinas */}
            <span className="absolute left-3 top-2 font-sans text-lg text-content-muted">
              {esquinas.arribaIzq}
            </span>
            <span className="absolute right-3 top-2 font-sans text-lg text-content-muted">
              {esquinas.arribaDer}
            </span>
            <span className="absolute bottom-2 left-3 font-sans text-lg text-content-muted">
              {esquinas.abajoIzq}
            </span>
            <span className="absolute bottom-2 right-3 font-sans text-lg text-content-muted">
              {esquinas.abajoDer}
            </span>

            {/* Un punto por propuesta consolidada, SIN autores (C7) */}
            {puntos.map((p) => {
              const izquierda = pct(p.x);
              const abajo = pct(p.y);
              // En la mitad derecha la etiqueta va a la izquierda del punto
              // para no desbordar el plano.
              const haciaIzquierda = izquierda > 55;
              return (
                <button
                  key={normalizar(p.etiqueta)}
                  type="button"
                  onClick={() => setAmpliado(p)}
                  aria-label={`Ampliar «${p.etiqueta}»`}
                  className={`absolute flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:shadow-focus ${haciaIzquierda ? 'flex-row-reverse' : ''}`}
                  style={{
                    left: `${izquierda}%`,
                    bottom: `${abajo}%`,
                    transform: haciaIzquierda
                      ? 'translate(calc(-100% + 0.625rem), 50%)'
                      : 'translate(-0.625rem, 50%)',
                  }}
                >
                  <span className="h-5 w-5 shrink-0 rounded-full bg-cyan" aria-hidden />
                  <span className="max-w-56 truncate font-display text-xl font-semibold leading-tight text-content">
                    {p.etiqueta}
                  </span>
                  {p.veces > 1 && (
                    <span className="shrink-0 rounded-full bg-surface-sunken px-2 font-sans text-base font-semibold tabular-nums text-content-muted">
                      ×{p.veces}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Etiqueta del eje X, centrada bajo el plano */}
          <p className="mt-2 text-center font-sans text-lg uppercase tracking-wide text-content-muted">
            {ejeXEtiqueta}
          </p>
        </div>
      </div>

      {mostrarGrupos && mesas > 0 && (
        <p className="mt-4 text-center font-sans text-lg text-content-muted">
          Calificaron <span className="font-semibold tabular-nums text-content">{mesas}</span>{' '}
          {mesas === 1 ? 'mesa de trabajo' : 'mesas de trabajo'}
        </p>
      )}

      <Ampliada abierta={ampliado != null} onCerrar={() => setAmpliado(null)}>
        {ampliado && (
          <div className="flex flex-col gap-6 text-center">
            <p className="font-display text-4xl font-semibold leading-tight text-content">
              {ampliado.etiqueta}
            </p>
            <div className="flex items-center justify-center gap-10">
              <p className="font-sans text-xl text-content-muted">
                {ejeYEtiqueta}:{' '}
                <span className="font-display font-semibold tabular-nums text-content">
                  {ampliado.y.toFixed(1)}
                </span>
              </p>
              <p className="font-sans text-xl text-content-muted">
                {ejeXEtiqueta}:{' '}
                <span className="font-display font-semibold tabular-nums text-content">
                  {ampliado.x.toFixed(1)}
                </span>
              </p>
              <p className="font-sans text-xl text-content-muted">
                Calificaciones:{' '}
                <span className="font-display font-semibold tabular-nums text-content">
                  {ampliado.veces}
                </span>
              </p>
            </div>
          </div>
        )}
      </Ampliada>
    </section>
  );
}
